import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Users, Shield, Scale, UserCheck, DollarSign, Globe, Copyright, Gavel, XCircle } from 'lucide-react';
import { trackPageView } from '../analytics';

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-8">
    <div className="flex items-center gap-3 mb-4">
      {Icon && <Icon className="w-6 h-6 text-blue-600" />}
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
    </div>
    <div className="text-gray-700 space-y-3 pl-9">
      {children}
    </div>
  </div>
);

const TermsOfServicePage = () => {
  useEffect(() => {
    trackPageView('terms_of_service');
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
          </div>
          <p className="text-gray-500 mt-2">Last updated: December 2024</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8">
          <Section icon={UserCheck} title="Eligibility">
            <p>
              You must be at least <strong>13 years old</strong> to use CoinCommons.
            </p>
            <p>By using CoinCommons, you confirm that:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>You are at least 13 years old and meet the minimum age required by the laws of your country of residence</li>
              <li>If you are between 13 and the age of majority in your jurisdiction, your legal guardian has reviewed and agrees to these Terms</li>
              <li>You are not barred from using the Services under any applicable laws</li>
              <li>You can form a binding contract with CoinCommons</li>
            </ul>
          </Section>

          <Section icon={Users} title="User Conduct">
            <p>By using CoinCommons, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Not publish false, misleading, or inaccurate information</li>
              <li>Not engage in spamming, scamming, or fraudulent activities</li>
              <li>Not post inappropriate, discriminatory, or abusive content</li>
              <li>Not solicit or promote illegal activities</li>
              <li>Respect other users and maintain civil discourse</li>
            </ul>
            <p className="text-sm text-gray-600 mt-3">
              CoinCommons reserves the right to remove any content that violates these guidelines
              and to suspend or terminate accounts of repeat offenders.
            </p>
          </Section>

          <Section icon={DollarSign} title="Not Financial Advice">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Important Disclaimer:</p>
              <p className="text-red-700 text-sm mt-1">
                Information on CoinCommons is for <strong>informational purposes only</strong> and
                does not constitute financial, investment, legal, or tax advice.
              </p>
            </div>
            <p className="mt-3">
              You acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>No content on this platform should be considered as a recommendation to buy, sell, or hold any cryptocurrency or digital asset</li>
              <li>Security assessments and risk indicators are community-generated opinions, not professional financial analysis</li>
              <li>You should consult a qualified financial advisor before making any investment decisions</li>
              <li>Cryptocurrency investments carry significant risk, including the potential loss of your entire investment</li>
            </ul>
          </Section>

          <Section icon={AlertTriangle} title="Content Disclaimer">
            <p>
              Content on this platform is community-generated and may include AI-assisted information.
              This includes but is not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Comments and discussions about addresses and tokens</li>
              <li>Security assessments and risk indicators</li>
              <li>Wallet labels and aliases</li>
              <li>Any other user-submitted information</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <p className="text-amber-800 font-medium">Important:</p>
              <p className="text-amber-700 text-sm mt-1">
                Always verify information independently before making any decisions.
                Information on this platform could be inaccurate, outdated, or intentionally misleading.
                Use it at your own discretion.
              </p>
            </div>
          </Section>

          <Section icon={Scale} title="Limitation of Liability">
            <p>
              CoinCommons and its operators accept no responsibility for any loss or damage
              arising from:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Use of information found on this platform</li>
              <li>Decisions made based on content or security assessments</li>
              <li>Interactions with addresses, tokens, or smart contracts referenced here</li>
              <li>Any financial losses or damages of any kind</li>
            </ul>
            <p className="mt-3">
              This platform is provided "as is" without warranties of any kind, either express or implied.
            </p>
          </Section>

          <Section icon={Copyright} title="Intellectual Property">
            <p>
              <strong>Platform Content:</strong> The CoinCommons name, logo, and platform design
              are owned by CoinCommons. You may not copy, modify, or distribute these without permission.
            </p>
            <p className="mt-3">
              <strong>User Content:</strong> By posting content on CoinCommons, you:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Retain ownership of your original content</li>
              <li>Grant CoinCommons a worldwide, non-exclusive, royalty-free license to use, display, and distribute your content on the platform</li>
              <li>Represent that you have the right to post such content and that it does not infringe on any third party's rights</li>
            </ul>
          </Section>

          <Section icon={Shield} title="Privacy & Data">
            <p>When using CoinCommons:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>
                <strong>Wallet Sign-In:</strong> We only store your public wallet address.
                We never have access to your private keys or funds.
              </li>
              <li>
                <strong>Email Sign-In:</strong> We store your email address for authentication purposes only.
              </li>
              <li>
                Your activity (comments, votes, aliases) is associated with your account
                and may be publicly visible.
              </li>
              <li>
                We may use cookies and similar technologies to improve your experience.
              </li>
            </ul>
            <p className="text-sm text-gray-600 mt-3">
              For more details, please review our <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</Link>.
            </p>
          </Section>

          <Section icon={Gavel} title="Indemnification">
            <p>
              You agree to indemnify and hold harmless CoinCommons, its operators, affiliates,
              and employees from any claims, damages, losses, or expenses (including legal fees)
              arising from:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Your use of the platform</li>
              <li>Your violation of these Terms</li>
              <li>Content you post or submit</li>
              <li>Your violation of any third party's rights</li>
            </ul>
          </Section>

          <Section icon={XCircle} title="Account Termination">
            <p>
              CoinCommons reserves the right to suspend or terminate your account at any time,
              with or without notice, for any reason, including but not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Violation of these Terms of Service</li>
              <li>Engaging in fraudulent or illegal activities</li>
              <li>Posting harmful, abusive, or misleading content</li>
              <li>Any conduct we deem harmful to other users or the platform</li>
            </ul>
          </Section>

          <Section icon={Globe} title="Governing Law & Disputes">
            <p>
              These Terms shall be governed by and construed in accordance with the laws
              of the United States, without regard to conflict of law principles.
            </p>
            <p className="mt-3">
              <strong>Dispute Resolution:</strong> Any disputes arising from these Terms or
              your use of CoinCommons shall first be attempted to be resolved through
              good-faith negotiation. If unresolved, disputes shall be submitted to binding
              arbitration, except that either party may seek injunctive relief in court
              for intellectual property violations.
            </p>
            <p className="mt-3">
              <strong>Class Action Waiver:</strong> You agree to resolve disputes with
              CoinCommons on an individual basis and waive any right to participate in
              a class action lawsuit or class-wide arbitration.
            </p>
          </Section>

          <div className="border-t border-gray-200 pt-6 mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Severability</h3>
            <p className="text-sm text-gray-600">
              If any provision of these Terms is found to be unenforceable or invalid,
              that provision shall be limited or eliminated to the minimum extent necessary,
              and the remaining provisions shall remain in full force and effect.
            </p>
          </div>

          <div className="border-t border-gray-200 pt-6 mt-6">
            <p className="text-sm text-gray-600">
              By using CoinCommons, you acknowledge that you have read, understood,
              and agree to be bound by these Terms of Service.
            </p>
            <p className="text-sm text-gray-500 mt-3">
              We may update these terms from time to time. Continued use of the platform
              after changes constitutes acceptance of the new terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
